import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import ContinueButtonBar from '../components/ContinueButtonBar';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { interpolateI18n } from '../utils/interpolateI18n';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import CareerUpApplicationChatComposer, {
  CareerUpApplicationDeclinedComposer,
} from './CareerUpApplicationChatComposer';
import CareerUpApplicationChatThread from './CareerUpApplicationChatThread';
import { useCareerUpApplicationFlow } from './useCareerUpApplicationFlow';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface CareerUpApplicationChatFlowProps {
  documentTitle: string;
  currentEmail?: string | null;
  invitationId?: string | null;
  onBack: () => void;
  onOpenConsentPdfRead?: () => Promise<void>;
  onOpenPdfPreview?: () => void;
  onCompleteContinue?: () => void | Promise<void>;
  onDeclinedContinue?: () => void | Promise<void>;
}

const CareerUpApplicationChatFlow: React.FC<CareerUpApplicationChatFlowProps> = ({
  documentTitle,
  currentEmail,
  invitationId,
  onBack,
  onOpenConsentPdfRead,
  onOpenPdfPreview,
  onCompleteContinue,
  onDeclinedContinue,
}) => {
  const { t } = useLanguage();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = useCareerUpApplicationFlow(currentEmail, invitationId, t);

  const [revealPhase, setRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const greeting = useMemo(
    () => interpolateI18n(t('submitDocuments.careerUpApplication.greetingPrefix'), { documentTitle }),
    [t, documentTitle]
  );

  const previewIntro = useMemo(
    () => interpolateI18n(t('submitDocuments.careerUpApplication.previewIntro'), { documentTitle }),
    [t, documentTitle]
  );

  const atComplete = flow.isComplete;
  const { showIntro: showCompleteMessage, showCta: showCompleteCta } = useStaggeredPreviewCta(atComplete);

  useEffect(() => {
    if (flow.isLoading) return;
    setRevealPhase(0);
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(() => setRevealPhase(2), FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS);
    const t3 = window.setTimeout(
      () => setRevealPhase(3),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 2
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [documentTitle, flow.isLoading]);

  useChatThreadScroll(threadEndRef, [
    revealPhase,
    flow.committed.length,
    flow.isComplete,
    flow.hasCardConflict,
    flow.assistantQuestionVisible,
    flow.editingCommitIndex,
    showCompleteMessage,
  ]);

  const canShowThread = revealPhase >= 3 && !flow.isLoading;
  const showDeclinedComposer = flow.hasCardConflict && onDeclinedContinue && canShowThread;

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && <AssistantBotRow animateIn>{greeting}</AssistantBotRow>}
            {revealPhase >= 2 && (
              <AssistantBotRow animateIn>{t('submitDocuments.careerUpApplication.intro')}</AssistantBotRow>
            )}
            {flow.isLoading ? (
              revealPhase >= 2 && (
                <AssistantBotRow animateIn>{t('submitDocuments.careerUpApplication.loading')}</AssistantBotRow>
              )
            ) : canShowThread ? (
              <CareerUpApplicationChatThread flow={flow} />
            ) : null}
            {atComplete && showCompleteMessage && (
              <AssistantBotRow animateIn>{t('submitDocuments.careerUpApplication.completeThankYou')}</AssistantBotRow>
            )}
            {atComplete && showCompleteCta && onOpenPdfPreview && (
              <AssistantBotRow animateIn>{previewIntro}</AssistantBotRow>
            )}
            <div ref={threadEndRef} aria-hidden />
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {showDeclinedComposer && (
              <CareerUpApplicationDeclinedComposer onContinue={() => void onDeclinedContinue()} />
            )}
            {atComplete && showCompleteCta && onOpenPdfPreview && (
              <PdfPreviewActionButton
                label={t('submitDocuments.careerUpApplication.previewPdf')}
                onClick={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onOpenPdfPreview();
                  })();
                }}
              />
            )}
            {atComplete && showCompleteCta && !onOpenPdfPreview && onCompleteContinue && (
              <ContinueButtonBar
                onContinue={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onCompleteContinue();
                  })();
                }}
              />
            )}
            {!flow.isComplete &&
              !flow.hasCardConflict &&
              !flow.isLoading &&
              canShowThread && (
                <CareerUpApplicationChatComposer
                  flow={flow}
                  onOpenConsentPdfRead={onOpenConsentPdfRead}
                />
              )}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default CareerUpApplicationChatFlow;
